import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { CalendarDays, Check, Gamepad2, MapPin, ShieldCheck, Ticket, Zap } from "lucide-react";
import { claimFreePass, listIntroSessions } from "@/lib/start-lead.functions";
import {
  startLeadFormSchema,
  captureStartUtm,
  readStartUtm,
  type StartLeadField,
} from "@/lib/start-lead.schema";
import { readStoredRefCode, storeRefCode } from "@/lib/referral-code";
import { formatEventDate } from "@/lib/events";

import { track } from "@/lib/analytics";
import logoAsset from "@/assets/logo.png.asset.json";

export const Route = createFileRoute("/start")({
  loader: () => listIntroSessions(),
  head: () => ({
    meta: [
      { title: "Your First FPV Flight is on Us — SquadFlight Las Vegas" },
      {
        name: "description",
        content:
          "First-time visitors get one free 1-Hour Proving Ground Intro session at SquadFlight's indoor micro-FPV gym in Las Vegas. Drones, goggles and coaching provided. No card required.",
      },

      { property: "og:title", content: "SquadFlight Proving Ground — Intro & Level 0 Training" },
      {
        property: "og:description",
        content:
          "See upcoming SquadFlight Proving Ground Intro and Level 0 training sessions in Las Vegas. Gear and coaching provided.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://squadflight.com/start" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://squadflight.com/start" }],
  }),
  errorComponent: StartFallback,
  notFoundComponent: StartFallback,
  component: StartPage,
});

function StartFallback() {
  return (
    <main className="min-h-screen bg-void px-5 py-24 text-center font-inter text-white">
      <h1 className="font-tech text-3xl font-bold">This page didn&rsquo;t load</h1>
      <p className="mt-3 text-white/60">Give it a refresh, or head to squadflight.com.</p>
      <Link to="/" className="mt-6 inline-block text-cyber underline underline-offset-4">
        Back to SquadFlight
      </Link>
    </main>
  );
}

const BENEFITS = [
  {
    icon: Gamepad2,
    kicker: "The Session",
    title: "The Proving Ground Intro",
    body: "A 1-hour guided session blending professional flight simulators with real-world micro-drone stick time. All gear provided.",
  },
  {
    icon: Zap,
    kicker: "The Progression",
    title: "The Next Step: Level 0",
    body: "This intro perfectly prepares you for our 2-hour Level 0 certification class, where you will earn the right to fly our on-site Cetus rental fleet.",
  },
  {
    icon: ShieldCheck,
    kicker: "The Environment",
    title: "Zero Damage, Pure Adrenaline",
    body: "100% ducted indoor micro-drones. No wind, no FAA rules, no intimidation.",
  },
];

function StartPage() {
  const { sessions } = Route.useLoaderData();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [company, setCompany] = useState(""); // honeypot
  const [errors, setErrors] = useState<Partial<Record<StartLeadField, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<{ code: string | null; already: boolean } | null>(null);
  const mountedAt = useRef(Date.now());
  const submit = useServerFn(claimFreePass);

  useEffect(() => {
    captureStartUtm();
    // A pilot's share link can land here as /start?ref=CODE too.
    const fromQuery = new URLSearchParams(window.location.search).get("ref");
    if (fromQuery) storeRefCode(fromQuery);
    track("start_page_viewed", { lead_source: readStartUtm() || "untagged" });
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    const parsed = startLeadFormSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<StartLeadField, string>> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as StartLeadField] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const result = await submit({
        data: {
          ...parsed.data,
          company,
          leadSourceRaw: readStartUtm(),
          refCode: readStoredRefCode() ?? "",
          elapsedMs: Date.now() - mountedAt.current,
        },
      });

      setClaimed({ code: result.promoCode, already: result.alreadyClaimed });
      track("start_pass_claimed", {
        lead_source: readStartUtm() || "untagged",
        already_claimed: result.alreadyClaimed,
      });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Please try again in a moment.");
      track("start_pass_failed", { lead_source: readStartUtm() || "untagged" });
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-lg border border-white/15 bg-void px-4 py-3 text-base text-white placeholder:text-white/35 outline-none transition-colors focus:border-cyber";

  return (
    <div className="min-h-screen bg-void font-inter text-white">
      {/* Sticky nav */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-void/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-3">
          <span className="flex items-center gap-2">
            <img
              src={logoAsset.url}
              alt="SquadFlight"
              className="h-7 w-auto"
              width={28}
              height={28}
            />
            <span className="font-tech text-lg font-bold tracking-[0.18em]">
              SQUAD<span className="text-cyber">FLIGHT</span>
            </span>
          </span>
          <a
            href="#claim"
            onClick={() => track("start_nav_cta_clicked", {})}
            className="rounded-full border border-cyber/60 px-4 py-2 font-tech text-xs font-bold uppercase tracking-[0.15em] text-cyber"
          >
            Claim Free Pass
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-20">
        {/* Hero */}
        <section className="pt-12">
          <p className="font-tech text-xs uppercase tracking-[0.35em] text-laser">
            First-Timer Intro Session
          </p>
          <h1 className="mt-3 font-tech text-[2.6rem] font-bold leading-[1.05] tracking-tight sm:text-6xl">
            Your First Flight is on Us.
          </h1>
          <p className="mt-4 text-lg text-cyber">
            One 1-Hour Proving Ground Intro session, free for first-time visitors. No card, no
            charge, nothing to pay.
          </p>
          <p className="mt-4 text-base leading-relaxed text-white/70">
            SquadFlight is an indoor micro-FPV drone gym in Las Vegas. On your intro session we
            provide the simulators, the micro-drones, the goggles and a coach — you just show up and
            hit the sticks. Sign up below and we&rsquo;ll email you the session dates to pick from;
            you confirm the one you want. Paid sessions and optional gear are priced separately and
            always shown before you commit.
          </p>

          <a
            href="#claim"
            onClick={() => track("start_hero_cta_clicked", {})}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyber px-7 py-4 font-tech text-base font-bold uppercase tracking-[0.12em] text-void sm:w-auto"
          >
            <Ticket className="size-5" />
            Claim My Free Session
          </a>
        </section>

        {/* How it works */}
        <section className="mt-14 grid gap-4 sm:grid-cols-3">
          {BENEFITS.map(({ icon: Icon, kicker, title, body }) => (
            <article
              key={title}
              className="rounded-2xl border border-white/10 bg-cockpit p-5 transition-colors hover:border-cyber/40"
            >
              <Icon className="size-6 text-cyber" aria-hidden="true" />
              <p className="mt-4 font-tech text-[0.7rem] uppercase tracking-[0.25em] text-laser">
                {kicker}
              </p>
              <h2 className="mt-1 font-tech text-xl font-bold">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{body}</p>
            </article>
          ))}
        </section>

        {/* Lead capture */}
        <section id="claim" className="mt-14 scroll-mt-20">
          <div className="rounded-2xl border border-cyber/30 bg-cockpit p-6">
            {claimed ? (
              <div className="text-center">
                <Check className="mx-auto size-9 text-cyber" />
                <h2 className="mt-4 font-tech text-2xl font-bold">
                  {!claimed.code
                    ? "We got your request."
                    : claimed.already
                      ? "Your pass is still good."
                      : "Your free pass is confirmed."}
                </h2>
                {claimed.code ? (
                  <>
                    <p className="mt-4 font-tech text-[0.7rem] uppercase tracking-[0.3em] text-white/50">
                      Your pass code
                    </p>
                    <p className="font-tech text-4xl font-bold tracking-[0.25em] text-cyber">
                      {claimed.code}
                    </p>
                  </>
                ) : null}
                <p className="mt-4 text-sm text-white/70">
                  {claimed.code
                    ? "We just emailed it to you along with the details. Bring it to your first session — your Pilot Number is issued to you in person that night."
                    : "Your pass code is still being issued. Give it a minute and try again, or email hello@squadflight.com and we'll sort it out."}
                </p>
                {!claimed.code && (
                  <button
                    type="button"
                    onClick={() => setClaimed(null)}
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 font-tech text-sm font-bold uppercase tracking-[0.12em] text-white"
                  >
                    Try again
                  </button>
                )}
                <Link
                  to="/events"
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-cyber/60 px-6 py-3 font-tech text-sm font-bold uppercase tracking-[0.12em] text-cyber"
                >
                  Pick a session
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <h2 className="font-tech text-2xl font-bold">Claim your free pass</h2>

                {/* Honeypot */}
                <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
                  <label htmlFor="start-company">Company</label>
                  <input
                    id="start-company"
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="start-first"
                      className="font-tech text-xs uppercase tracking-[0.2em] text-white/60"
                    >
                      First Name
                    </label>
                    <input
                      id="start-first"
                      autoComplete="given-name"
                      value={form.firstName}
                      maxLength={60}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                      className={`mt-2 ${inputClass}`}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-laser">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="start-last"
                      className="font-tech text-xs uppercase tracking-[0.2em] text-white/60"
                    >
                      Last Name
                    </label>
                    <input
                      id="start-last"
                      autoComplete="family-name"
                      value={form.lastName}
                      maxLength={60}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                      className={`mt-2 ${inputClass}`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-laser">{errors.lastName}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="start-email"
                      className="font-tech text-xs uppercase tracking-[0.2em] text-white/60"
                    >
                      Email Address
                    </label>
                    <input
                      id="start-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.email}
                      maxLength={255}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={`mt-2 ${inputClass}`}
                    />
                    {errors.email && <p className="mt-1 text-sm text-laser">{errors.email}</p>}
                  </div>
                  <div>
                    <label
                      htmlFor="start-phone"
                      className="font-tech text-xs uppercase tracking-[0.2em] text-white/60"
                    >
                      Phone Number <span className="normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      id="start-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={form.phone}
                      maxLength={30}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className={`mt-2 ${inputClass}`}
                    />
                    {errors.phone && <p className="mt-1 text-sm text-laser">{errors.phone}</p>}
                  </div>
                </div>

                {serverError && (
                  <p className="mt-4 rounded-lg border border-laser/40 bg-laser/10 px-4 py-3 text-sm text-white">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 w-full rounded-full bg-cyber px-7 py-4 font-tech text-base font-bold uppercase tracking-[0.12em] text-void disabled:opacity-60"
                >
                  {submitting ? "Claiming…" : "Claim My Free Session"}
                </button>
                <p className="mt-3 text-center text-xs leading-relaxed text-white/50">
                  No payment now and no card required. We&rsquo;ll email you the intro session dates
                  and occasional SquadFlight updates — a few messages a month at most, and every one
                  has an unsubscribe link. Your official SquadFlight Pilot Number is issued in
                  person at your first event. See our{" "}
                  <Link to="/terms" className="text-cyber underline underline-offset-4">
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-cyber underline underline-offset-4">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            )}
          </div>
        </section>

        {/* Upcoming Proving Ground sessions */}
        <section className="mt-14">
          <h2 className="font-tech text-2xl font-bold">Upcoming Proving Ground Training</h2>
          <p className="mt-2 text-sm leading-relaxed text-white/70">
            Start with INTRO for your first guided flight, then continue to LVL0 certification.
            House drones, goggles and coaching are provided for both.
          </p>
          <div className="mt-5 space-y-3">
            {sessions.length === 0 ? (
              <p className="rounded-2xl border border-white/10 bg-cockpit p-5 text-sm text-white/70">
                New INTRO and LVL0 dates drop regularly — claim your pass above and we&rsquo;ll
                email you when the next training opens.
              </p>
            ) : (
              sessions.map((session) => (
                <Link
                  key={session.id}
                  to="/events/$slug"
                  params={{ slug: session.slug }}
                  onClick={() => track("start_session_clicked", { slug: session.slug })}
                  className="block rounded-2xl border border-white/10 bg-cockpit p-5 transition-colors hover:border-cyber/50"
                >
                  <p className="font-tech text-lg font-bold">{session.title}</p>
                  <p className="mt-2 flex items-center gap-2 text-sm text-white/70">
                    <CalendarDays className="size-4 text-cyber" />
                    {formatEventDate(session.starts_at, session.ends_at)}
                  </p>
                  {session.location && (
                    <p className="mt-1 flex items-center gap-2 text-sm text-white/70">
                      <MapPin className="size-4 text-cyber" />
                      {session.location}
                    </p>
                  )}
                </Link>
              ))
            )}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-white/45">
            The free pass applies to one Proving Ground INTRO session per first-time visitor. LVL0
            certification is $99, with your $30 INTRO payment credited toward it when applicable.
            House gear is provided for both.
          </p>
        </section>
      </main>

      <footer className="border-t border-white/10 px-5 py-8 text-center text-xs text-white/45">
        <p>SquadFlight FPV · Las Vegas, NV</p>
        <p className="mt-2">
          <a href="mailto:hello@squadflight.com" className="underline underline-offset-4">
            hello@squadflight.com
          </a>
        </p>
        <p className="mt-2">
          Exact venue address is sent with your session confirmation, or email us with questions.
        </p>
        <p className="mt-2">
          © {new Date().getFullYear()} SquadFlight FPV ·{" "}
          <Link to="/contact" className="text-cyber underline underline-offset-4">
            Contact
          </Link>{" "}
          ·{" "}
          <Link to="/terms" className="text-cyber underline underline-offset-4">
            Terms
          </Link>{" "}
          ·{" "}
          <Link to="/privacy" className="text-cyber underline underline-offset-4">
            Privacy Policy
          </Link>
        </p>
      </footer>
    </div>
  );
}
